import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MusicLoader from "../../../helper/MusicLoader";
import MusicMikeIcon from "../../../assets/images/icons/music-mike.svg";
import { showToast } from "../../../helper/ShowToast";
import { useSmartLinkList } from "../../../queries/artistManger/smartLink.query";
import * as actions from "../../../redux/actions/growthEngine";
import TourLink from "../LinkAndFanHub/BuildTourLink/TourLink";

const timeAgo = (date) => {
    const now = new Date();
    const past = new Date(date);
    const seconds = Math.floor((now - past) / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30.44);
    const years = Math.floor(days / 365.25);

    if (seconds < 5) return "just now";
    if (seconds < 60) return `${seconds} seconds ago`;
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
    if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;
    return `${years} year${years > 1 ? "s" : ""} ago`;
};

export const TourLinkStep = () => {
    const dispatch = useDispatch();
    const { formData } = useSelector((state) => state.growthEngine);
    const [page, setPage] = useState(formData.selectedTourLinkPage || 1);
    const [view, setView] = useState("list");
    const [editLinkId, setEditLinkId] = useState(null);
    const limit = 5;

    const {
        data: smartlinkListData,
        isLoading,
        refetch,
    } = useSmartLinkList({ page, limit, type: "tourlink" });

    const { total = 0, smartlinkList = [] } = smartlinkListData || {};
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const tourLinks = smartlinkList;
    const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1;
    const rangeEnd = Math.min(page * limit, total);
    const hasMultiplePages = totalPages > 1;

    const toggleTourLinkSelection = (link) => {
        const isSelected =
            String(formData.selectedTourLinkId) === String(link._id);

        if (isSelected) {
            dispatch(
                actions.updateFormData({
                    selectedTourLinkId: "",
                    selectedTourLinkPage: 1,
                    destinationUrl: "",
                    selectedTargetImage: "",
                    selectedTargetTitle: "",
                })
            );
            showToast("info", "Tour link unselected");
            return;
        }

        if (!link?.fullLink) {
            showToast("error", "Tour link URL is missing");
            return;
        }
        dispatch(
            actions.updateFormData({
                selectedTourLinkId: link._id,
                selectedTourLinkPage: page,
                destinationUrl: link.fullLink,
                selectedTargetImage: link.image || "",
                selectedTargetTitle: link.title || "",
            })
        );
        showToast("success", "Tour link selected for your ad");
    };

    const handleCreateSuccess = (createdLink) => {
        const link = createdLink?._id || createdLink?.id ? createdLink : createdLink?.data;
        const linkId = link?._id || link?.id;
        if (linkId && link?.fullLink) {
            dispatch(
                actions.updateFormData({
                    selectedTourLinkId: linkId,
                    destinationUrl: link.fullLink,
                    selectedTargetImage: link.image || "",
                    selectedTargetTitle: link.title || "",
                })
            );
        }
        setView("list");
        setEditLinkId(null);
        refetch();
    };

    const handleEditSuccess = (updatedLink) => {
        const link = updatedLink?._id || updatedLink?.id ? updatedLink : updatedLink?.data;
        const linkId = link?._id || link?.id;
        if (
            linkId &&
            link?.fullLink &&
            String(formData.selectedTourLinkId) === String(linkId)
        ) {
            dispatch(
                actions.updateFormData({
                    destinationUrl: link.fullLink,
                    selectedTargetImage: link.image || "",
                    selectedTargetTitle: link.title || "",
                })
            );
        }
        setView("list");
        setEditLinkId(null);
        refetch();
    };

    if (view === "create") {
        return (
            <div className="font-inter mb-8 xl:mb-[61px]" data-tour="tour-link-step">
                <div className="heading text-white font-medium text-center mb-[28.5px]">
                    <h5 className="text-[22.5px] mb-1">Create Tour Link</h5>
                    <div className="description text-white text-opacity-80 text-[15px]">
                        Build your tour link here and continue with your campaign.
                    </div>
                </div>
                <div className="info-box bg-white rounded-xl overflow-hidden">
                    <TourLink
                        embedded
                        hideAnalytics
                        onBack={() => {
                            setView("list");
                            setEditLinkId(null);
                        }}
                        onSuccess={handleCreateSuccess}
                    />
                </div>
            </div>
        );
    }

    if (view === "edit" && editLinkId) {
        return (
            <div className="font-inter mb-8 xl:mb-[61px]" data-tour="tour-link-step">
                <div className="heading text-white font-medium text-center mb-[28.5px]">
                    <h5 className="text-[22.5px] mb-1">Edit Tour Link</h5>
                    <div className="description text-white text-opacity-80 text-[15px]">
                        Update your tour link and return to campaign setup.
                    </div>
                </div>
                <div className="info-box bg-white rounded-xl overflow-hidden">
                    <TourLink
                        embedded
                        hideAnalytics
                        linkId={editLinkId}
                        onBack={() => {
                            setView("list");
                            setEditLinkId(null);
                        }}
                        onSuccess={handleEditSuccess}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="font-inter mb-8 xl:mb-[61px]" data-tour="tour-link-step">
            <div className="heading text-white font-medium text-center mb-[28.5px]">
                <h5 className="text-[22.5px] mb-1">Choose your Tour Link</h5>
                <div className="description text-white text-opacity-80 text-[15px]">
                    Select a tour link for your event ad, create a new one, or edit an existing link.
                </div>
            </div>

            <div className="info-box bg-white rounded-xl p-4 lg:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 pb-4 border-b border-theme_clr2 border-opacity-30">
                    <div>
                        <div className="title-name text-[22.69px] font-medium text-theme_clr1 mb-1">
                            Your Tour Links
                        </div>
                        <div className="description font-medium text-theme_clr1 text-opacity-55 text-[15px]">
                            Pick one link to use as your ad destination URL.
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn-custom flex text-nowrap items-center justify-center gap-2 !text-base !rounded-full shrink-0"
                        onClick={() => {
                            setEditLinkId(null);
                            setView("create");
                        }}
                    >
                        <img src={MusicMikeIcon} alt="Tour Link" />
                        Create Tour Link
                    </button>
                </div>

                <div className="border-[2px] border-theme_clr1 rounded-xl px-3 md:px-4 py-2 bg-white">
                    {isLoading ? (
                        <div className="flex justify-center items-center py-10">
                            <MusicLoader />
                        </div>
                    ) : tourLinks.length === 0 ? (
                        <div className="text-center text-lg font-bold text-theme_clr3 py-10">
                            No Tour Links found. Create one to continue.
                        </div>
                    ) : (
                        <div className="info-card-wrapper divide-y divide-theme_clr2 divide-opacity-55 font-jakarta">
                            {tourLinks.map((link) => {
                                const isSelected =
                                    String(formData.selectedTourLinkId) === String(link._id);

                                return (
                                    <div
                                        key={link._id}
                                        className={`info-card py-4 rounded-lg transition-colors ${
                                            isSelected
                                                ? "bg-theme_clr2/10 ring-2 ring-theme_clr2 ring-inset px-2 -mx-1"
                                                : ""
                                        }`}
                                    >
                                        <div className="flex flex-col lg:flex-row flex-wrap lg:items-center justify-between gap-4">
                                            <div className="flex items-start gap-3 min-w-0">
                                                <img
                                                    src={link?.image}
                                                    alt={link?.title}
                                                    className="w-14 h-[72px] rounded-lg object-cover flex-shrink-0"
                                                />
                                                <div className="text-theme_clr1 min-w-0">
                                                    <div className="font-bold text-base capitalize mb-1 flex items-center gap-2 flex-wrap">
                                                        <span>{link?.title}</span>
                                                        {isSelected && (
                                                            <span className="inline-flex items-center gap-1 bg-theme_clr2 text-white text-xs font-bold px-2 py-1 rounded">
                                                                <i className="bi bi-check-circle-fill"></i>
                                                                Selected
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-bold mb-[6px]">
                                                        <span>Created {timeAgo(link?.createdAt)} –</span>
                                                        <span>(Updated {timeAgo(link?.updatedAt)})</span>
                                                    </div>

                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <a
                                                            href={link?.fullLink}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="bg-theme_clr2 bg-opacity-55 font-bold text-sm rounded flex items-center gap-6 px-3 py-1 max-w-full"
                                                        >
                                                            <span className="truncate">{link?.fullLink}</span>
                                                            <span>
                                                                <i className="bi bi-link-45deg"></i>
                                                            </span>
                                                        </a>
                                                        <span>
                                                            <i className="bi bi-box-arrow-up-right"></i>
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="font-bold flex items-center flex-wrap gap-x-4 lg:gap-x-6 gap-y-2 text-theme_clr1 text-base">
                                                <button
                                                    type="button"
                                                    className={`px-4 py-2 rounded-full border-2 font-semibold transition ${
                                                        isSelected
                                                            ? "bg-theme_clr1 border-theme_clr1 text-white hover:bg-transparent hover:text-theme_clr1"
                                                            : "border-theme_clr1 text-theme_clr1 hover:bg-theme_clr1 hover:text-white"
                                                    }`}
                                                    onClick={() => toggleTourLinkSelection(link)}
                                                >
                                                    {isSelected ? "Unselect" : "Select"}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="hover:text-theme_clr3 transition"
                                                    onClick={() => {
                                                        setEditLinkId(link._id);
                                                        setView("edit");
                                                    }}
                                                >
                                                    Edit Site
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {!isLoading && total > 0 && (
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-7 pt-4 border-t border-theme_clr2 border-opacity-30">
                            <span className="text-sm font-medium text-theme_clr1 text-opacity-70">
                                Showing {rangeStart}–{rangeEnd} of {total} tour link{total !== 1 ? "s" : ""}
                            </span>

                            {hasMultiplePages && (
                                <div className="flex items-center justify-center gap-2">
                                    <button
                                        type="button"
                                        disabled={page <= 1}
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        className={`text-white border rounded-md px-4 py-2 ${
                                            page <= 1
                                                ? "bg-gray-400 cursor-not-allowed"
                                                : "bg-theme_clr3 hover:bg-theme_clr1"
                                        }`}
                                    >
                                        <i className="bi bi-chevron-double-left"></i>
                                    </button>

                                    <span className="bg-slate-100 text-theme_clr4 border text-base font-medium rounded-md px-4 py-2">
                                        Page {page} of {totalPages}
                                    </span>

                                    <button
                                        type="button"
                                        disabled={page >= totalPages}
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        className={`text-white border rounded-md px-4 py-2 ${
                                            page >= totalPages
                                                ? "bg-gray-400 cursor-not-allowed"
                                                : "bg-theme_clr3 hover:bg-theme_clr1"
                                        }`}
                                    >
                                        <i className="bi bi-chevron-double-right"></i>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
